import React, { useMemo, useState } from "react";
import {
  Stack,
  Group,
  Text,
  Button,
  Badge,
  Paper,
  Box,
  Anchor,
  Loader,
  ScrollArea,
} from "@mantine/core";
import { Link } from "react-router-dom";
import moment from "moment-timezone";
import { useSalon } from "../../context/SalonContext";
import { ActivityType } from "../../types/client";
import {
  ActivityEvent,
  ActivityCard,
  ActivityServicePackage,
  ActivityGiftCard,
  ActivityPayment,
  TransactionActivityEvent,
  GiftCardClaimEvent,
} from "../../types/clientActivity";
import { formatCurrency } from "../../utils/cashDrawerUtils";
import { appointmentStatusDisplay } from "../calendar/CalendarView/booking/guards";
import { getLineDiscountDollars } from "../../utils/discountUtils";
import { formatDiscountValue } from "../../utils/discountFormatting";
import { getLineDiscountEntries } from "../../utils/lineItemDiscounts";
import styles from "./ActivityTimeline.module.css";

interface ActivityTimelineProps {
  /** Prefetched activity events (newest first) from the dedicated endpoint. */
  events: ActivityEvent[];
  /** Cursor for the next page, or null when the history is exhausted. */
  nextCursor: string | null;
  /** Fetch + append the next page. */
  onLoadMore: () => void;
  /** First page still in flight (only relevant before any event arrives). */
  loading?: boolean;
  /** A "See More" fetch is in flight. */
  loadingMore?: boolean;
  /** Show per-transaction loyalty points (only when the program is active). */
  showLoyaltyPoints?: boolean;
  /** The client whose timeline this is — used to mark a self-recipient. */
  thisClientId?: string;
  /**
   * Open a transaction's detail modal by UUID. When provided (drawer context),
   * the "Transaction #" link delegates to the caller (the calendar opens its own
   * modal). When omitted, ClientDetails opens the modal in place on the page.
   * Gift-card-claim cards carry no transaction UUID, so their number is never a
   * link regardless.
   */
  onOpenTransaction?: (transactionId: string) => void;
}

const clientProfilePath = (clientId: string | number) =>
  `/admin/clients/${clientId}`;

const titleCaseMethod = (method?: string | null): string => {
  if (!method) return "";
  return method
    .split(" ")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(" ");
};

/** Tender label: "Visa •••• 6760" for card payments, else the titlecased
 *  method ("Cash"). Never renders "•••• null" — card fields are null for
 *  non-card tenders and pre-backfill payments. */
const tenderLabel = (
  cardBrand?: string | null,
  cardLast4?: string | null,
  method?: string | null,
): string => {
  if (cardBrand && cardLast4) return `${cardBrand} •••• ${cardLast4}`;
  return titleCaseMethod(method);
};

const firstPositivePayment = (
  payments: ActivityPayment[],
): ActivityPayment | undefined => payments.find((p) => (p.amount ?? 0) > 0);

/** Footer line, fields joined on " · ", dropping any that are absent. */
const footerText = (parts: (string | null | undefined)[]): string =>
  parts.filter((p): p is string => !!p).join(" · ");

const ActivityTimeline: React.FC<ActivityTimelineProps> = ({
  events,
  nextCursor,
  onLoadMore,
  loading = false,
  loadingMore = false,
  showLoyaltyPoints = false,
  thisClientId,
  onOpenTransaction,
}) => {
  const { businessInfo } = useSalon() as {
    businessInfo: { timezone?: string };
  };
  const timezone = businessInfo?.timezone || "America/New_York";

  const [activeFilter, setActiveFilter] = useState<ActivityType>("all");

  const formatDateTime = (dateStr: string, format: string) =>
    moment.utc(dateStr).tz(timezone).format(format);

  // Points render only once at least one transaction carries the rollup —
  // backends without it omit the field on every row.
  const showPoints =
    showLoyaltyPoints &&
    events.some(
      (e) =>
        e.event_type === "transaction" &&
        e.transaction.loyalty_points_earned !== undefined &&
        e.transaction.loyalty_points_earned !== null,
    );

  // Flatten the event stream into one card per sellable thing. Order within a
  // transaction: packages, then gift cards, then the appointment/quick-sale.
  const cards = useMemo((): ActivityCard[] => {
    const out: ActivityCard[] = [];
    events.forEach((event, idx) => {
      if (event.event_type === "gift_card_claim") {
        out.push({
          id: `${idx}:claim`,
          type: "giftcardclaim",
          created_dt: event.created_dt,
          event,
        });
        return;
      }
      const { line_items } = event;
      (line_items.service_packages || []).forEach((sp, i) => {
        out.push({
          id: `${idx}:package:${sp.client_service_package_id || i}`,
          type: "package",
          created_dt: event.created_dt,
          event,
          packageItem: sp,
        });
      });
      (line_items.gift_cards || []).forEach((gc, i) => {
        out.push({
          id: `${idx}:giftcard:${gc.gift_card_id || i}`,
          type: "giftcard",
          created_dt: event.created_dt,
          event,
          giftCardItem: gc,
        });
      });
      if ((line_items.booked_services || []).length > 0) {
        out.push({
          id: `${idx}:appointment`,
          type: "appointment",
          created_dt: event.created_dt,
          event,
        });
      } else if ((line_items.products || []).length > 0) {
        out.push({
          id: `${idx}:quicksale`,
          type: "quicksale",
          created_dt: event.created_dt,
          event,
        });
      }
    });
    return out;
  }, [events]);

  const filteredCards = useMemo(() => {
    if (activeFilter === "appointments")
      return cards.filter((c) => c.type === "appointment");
    if (activeFilter === "quicksales")
      return cards.filter((c) => c.type === "quicksale");
    if (activeFilter === "packages")
      return cards.filter((c) => c.type === "package");
    if (activeFilter === "giftcards")
      // Both a gift-card sale and a claim are gift-card activity.
      return cards.filter(
        (c) => c.type === "giftcard" || c.type === "giftcardclaim",
      );
    return cards;
  }, [cards, activeFilter]);

  const groupedCards = useMemo(() => {
    const groups: Record<string, ActivityCard[]> = {};
    filteredCards.forEach((card) => {
      // Group in the salon's timezone so the month header can't disagree with
      // the salon-tz date printed on each card (a card near a month boundary
      // would otherwise land under the wrong month in the viewer's local tz).
      const monthKey = moment
        .utc(card.created_dt)
        .tz(timezone)
        .format("MMMM YYYY");
      if (!groups[monthKey]) groups[monthKey] = [];
      groups[monthKey].push(card);
    });
    return groups;
  }, [filteredCards, timezone]);

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "green";
      case "confirmed":
      case "arrived":
        return "var(--color-primary)";
      case "cancelled":
        return "gray";
      case "no-show":
        return "red";
      case "pending":
        return "yellow";
      default:
        return "gray";
    }
  };

  /** Paid (filled teal) / Voided (neutral) / Open (amber tint) pill for sale-type cards. */
  const renderSalePill = (status: string) => {
    if (status?.toLowerCase() === "void") {
      return (
        <Badge size="sm" variant="light" color="gray">
          Voided
        </Badge>
      );
    }
    if (status?.toLowerCase() === "paid") {
      return (
        <Badge color="var(--brand-teal)" variant="filled" size="sm">
          Paid
        </Badge>
      );
    }
    return (
      <Badge size="sm" className={styles.openPill}>
        Open
      </Badge>
    );
  };

  const openTransaction = (uuid?: string | number | null) => {
    if (uuid != null && onOpenTransaction) onOpenTransaction(String(uuid));
  };

  // Footer / payment line: tender · sold by · Transaction #<ref>, dropping any
  // absent field. The Transaction # opens the full-screen TransactionDetailModal
  // (via onOpenTransaction) whenever a transaction UUID is available; gift-card
  // claim cards describe the ORIGINAL sale and carry no UUID, so their number
  // renders as plain text. Returns null when there is nothing to show.
  const renderTxnLine = (
    preParts: (string | null | undefined)[],
    referenceId: string | null,
    txnUuid?: string | number | null,
    opts?: { size?: string; c?: string; mt?: string | number },
  ) => {
    const pre = footerText(preParts);
    const hasRef = !!referenceId;
    if (!pre && !hasRef) return null;
    const { size = "xs", c = "dimmed", mt = "md" } = opts || {};
    const clickable = hasRef && txnUuid != null && !!onOpenTransaction;
    return (
      <Text size={size} c={c} mt={mt}>
        {pre}
        {pre && hasRef ? " · " : ""}
        {hasRef &&
          (clickable ? (
            <Anchor
              component="button"
              type="button"
              inherit
              onClick={() => openTransaction(txnUuid)}
            >
              {`Transaction #${referenceId}`}
            </Anchor>
          ) : (
            <Text span inherit>
              {`Transaction #${referenceId}`}
            </Text>
          ))}
      </Text>
    );
  };

  const renderPackageCard = (card: ActivityCard) => {
    const event = card.event as TransactionActivityEvent;
    const sp = card.packageItem as ActivityServicePackage;
    const payment = firstPositivePayment(event.payments || []);
    return (
      <Paper
        key={card.id}
        className={styles.timelineCard}
        withBorder
        p="md"
        data-type="package"
      >
        <Group justify="space-between" align="flex-start" mb="xs">
          <Text fw={700} size="lg" c="dark.8">
            Package Sale
          </Text>
          {renderSalePill(event.transaction.transaction_status)}
        </Group>
        <Text size="xs" c="dimmed" mb="sm">
          {formatDateTime(card.created_dt, "MMM D, YYYY h:mm a")}
        </Text>
        <Stack gap={6}>
          <Text size="sm">
            {sp.quantity}× {sp.name}
            {" | "}
            <Text span fw={600} className={styles.money}>
              {formatCurrency(sp.amount)}
            </Text>
          </Text>
          {(sp.services || []).map((svc) => {
            const expiry = !sp.expires_at
              ? "No expiry"
              : `Expires ${formatDateTime(sp.expires_at, "MMM D, YYYY")}`;
            return (
              <Text key={String(svc.salon_service_id)} size="sm" c="dark.6">
                {svc.service_name} · {svc.quantity_remaining} of{" "}
                {svc.quantity_total} remaining · {expiry}
              </Text>
            );
          })}
        </Stack>
        {renderTxnLine(
          [
            payment
              ? tenderLabel(
                  payment.card_brand,
                  payment.card_last4,
                  payment.method,
                )
              : null,
            sp.sold_by ? `Sold by ${sp.sold_by}` : null,
          ],
          event.transaction.reference_id,
          event.transaction.id,
        )}
      </Paper>
    );
  };

  const renderRecipientLine = (gc: ActivityGiftCard) => {
    const name = gc.recipient_name;
    if (!name) return null;
    const isSelf =
      !!thisClientId &&
      !!gc.recipient_client_id &&
      String(gc.recipient_client_id) === String(thisClientId);
    if (isSelf) {
      return (
        <Text size="sm" c="dark.6">
          Recipient {name} (self)
        </Text>
      );
    }
    if (gc.claimed && gc.recipient_client_id) {
      return (
        <Text size="sm" c="dark.6">
          Recipient{" "}
          <Anchor
            component={Link}
            to={clientProfilePath(gc.recipient_client_id)}
          >
            {name}
          </Anchor>
        </Text>
      );
    }
    return (
      <Text size="sm" c="dark.6">
        Recipient {name}
        {gc.recipient_email ? ` · ${gc.recipient_email}` : ""}
      </Text>
    );
  };

  const renderGiftCardCard = (card: ActivityCard) => {
    const event = card.event as TransactionActivityEvent;
    const gc = card.giftCardItem as ActivityGiftCard;
    const payment = firstPositivePayment(event.payments || []);
    return (
      <Paper
        key={card.id}
        className={styles.timelineCard}
        withBorder
        p="md"
        data-type="giftcard"
      >
        <Group justify="space-between" align="flex-start" mb="xs">
          <Text fw={700} size="lg" c="dark.8">
            Gift Card Sale
          </Text>
          {renderSalePill(event.transaction.transaction_status)}
        </Group>
        <Text size="xs" c="dimmed" mb="sm">
          {formatDateTime(card.created_dt, "MMM D, YYYY h:mm a")}
        </Text>
        <Stack gap={6}>
          <Text size="sm">
            {formatCurrency(gc.face_value)} Gift Card
            {" | "}
            <Text span fw={600} className={styles.money}>
              {formatCurrency(gc.amount)}
            </Text>
          </Text>
          {renderRecipientLine(gc)}
          <Text size="sm" c="dark.6">
            Code{" "}
            <Text span className={styles.code}>
              {gc.code}
            </Text>{" "}
            · Balance {formatCurrency(gc.balance)} of{" "}
            {formatCurrency(gc.face_value)}
          </Text>
        </Stack>
        {renderTxnLine(
          [
            payment
              ? tenderLabel(
                  payment.card_brand,
                  payment.card_last4,
                  payment.method,
                )
              : null,
            gc.sold_by ? `Sold by ${gc.sold_by}` : null,
          ],
          event.transaction.reference_id,
          event.transaction.id,
        )}
      </Paper>
    );
  };

  const renderClaimCard = (card: ActivityCard) => {
    const event = card.event as GiftCardClaimEvent;
    const gc = event.gift_card;
    const sale = event.original_sale;
    return (
      <Paper
        key={card.id}
        className={styles.timelineCard}
        withBorder
        p="md"
        data-type="giftcardclaim"
      >
        <Group justify="space-between" align="flex-start" mb="xs">
          <Text fw={700} size="lg" c="dark.8">
            Gift Card Claim
          </Text>
          <Badge color="var(--brand-teal)" variant="filled" size="sm">
            Paid
          </Badge>
        </Group>
        <Text size="xs" c="dimmed" mb="sm">
          {formatDateTime(card.created_dt, "MMM D, YYYY h:mm a")}
        </Text>
        <Stack gap={6}>
          <Text size="sm">
            {formatCurrency(gc.face_value)} Gift Card
            {" | "}
            <Text span fw={600} className={styles.money}>
              {formatCurrency(gc.face_value)}
            </Text>
          </Text>
          {event.purchaser.name && (
            <Text size="sm" c="dark.6">
              Purchaser{" "}
              {event.purchaser.client_id ? (
                <Anchor
                  component={Link}
                  to={clientProfilePath(event.purchaser.client_id)}
                >
                  {event.purchaser.name}
                </Anchor>
              ) : (
                event.purchaser.name
              )}
            </Text>
          )}
          <Text size="sm" c="dark.6">
            Code{" "}
            <Text span className={styles.code}>
              {gc.code}
            </Text>{" "}
            · Balance {formatCurrency(gc.balance)} of{" "}
            {formatCurrency(gc.face_value)}
          </Text>
        </Stack>
        {renderTxnLine(
          [
            tenderLabel(sale.card_brand, sale.card_last4, sale.method),
            sale.sold_by ? `Sold by ${sale.sold_by}` : null,
          ],
          sale.reference_id,
          // Claim describes the original sale; opening its Transaction# opens
          // that sale's detail modal (same as every other card type).
          sale.transaction_id,
        )}
      </Paper>
    );
  };

  const renderQuickSaleCard = (card: ActivityCard) => {
    const event = card.event as TransactionActivityEvent;
    const payment = firstPositivePayment(event.payments || []);
    return (
      <Paper
        key={card.id}
        className={styles.timelineCard}
        withBorder
        p="md"
        data-type="quicksale"
      >
        <Group justify="space-between" align="flex-start" mb="xs">
          <Text fw={700} size="lg" c="dark.8">
            Quick Sale
          </Text>
          {renderSalePill(event.transaction.transaction_status)}
        </Group>
        <Text size="xs" c="dimmed" mb="sm">
          {formatDateTime(card.created_dt, "MMM D, YYYY h:mm a")}
        </Text>
        <Stack gap="xs">
          {(event.line_items.products || []).map((product) => (
            <Box key={String(product.id)}>
              <Text size="sm">
                Purchased {product.quantity}x {product.name}
              </Text>
              {getLineDiscountEntries(product).map((discount, index) => (
                <Text size="xs" c="dimmed" key={discount.id ?? index}>
                  {discount.name} (Discount/Adjustment) --{" "}
                  {formatDiscountValue(discount.discount_type, discount.amount)}
                </Text>
              ))}
            </Box>
          ))}
        </Stack>
        {renderTxnLine(
          [
            payment
              ? tenderLabel(
                  payment.card_brand,
                  payment.card_last4,
                  payment.method,
                )
              : null,
          ],
          event.transaction.reference_id,
          event.transaction.id,
        )}
      </Paper>
    );
  };

  const renderAppointmentCard = (card: ActivityCard) => {
    const event = card.event as TransactionActivityEvent;
    const bookedServices = event.line_items.booked_services || [];
    const products = event.line_items.products || [];
    const appointmentStatus = event.transaction.appointment_status || "Unknown";
    // Display-only "Accepted (Unconfirmed)" (Accepted within 48h of start).
    const statusDisplay = appointmentStatusDisplay(
      appointmentStatus,
      event,
      timezone,
    );
    const statusLabel = statusDisplay.label;
    const statusColor = statusDisplay.awaitingConfirmation
      ? statusDisplay.color
      : getStatusColor(appointmentStatus);
    const primaryStaffName =
      bookedServices.length > 0 ? bookedServices[0].staff_name : "Unknown";
    const startDt =
      bookedServices.length > 0 ? bookedServices[0].start_dt : null;

    const servicesTotal = bookedServices.reduce(
      (total, service) =>
        total +
        service.unit_price -
        getLineDiscountDollars(service, service.unit_price) +
        (service.tip || 0),
      0,
    );

    const productsTotal = products.reduce((total, product) => {
      const subtotal = product.unit_price * product.quantity;
      return total + subtotal - getLineDiscountDollars(product, subtotal);
    }, 0);

    const totalChargedPrice = servicesTotal + productsTotal;
    const payment = firstPositivePayment(event.payments || []);

    return (
      <Paper
        key={card.id}
        className={styles.timelineCard}
        withBorder
        p="md"
        data-type="appointment"
      >
        <Group justify="space-between" align="flex-start" mb="xs">
          <Text fw={700} size="lg" c="dark.8">
            Appointment
          </Text>
          <Badge color={statusColor} variant="filled">
            {statusLabel}
          </Badge>
        </Group>

        {startDt && (
          <Text size="sm" c="dimmed">
            {formatDateTime(startDt, "MMM D, YYYY h:mm a")} | {primaryStaffName}
          </Text>
        )}

        <Text size="sm" mt="xs">
          {bookedServices.map((s) => s.name).join(", ")}
          {totalChargedPrice > 0
            ? ` | ${formatCurrency(totalChargedPrice)}`
            : ""}
          {" | "}
          {statusLabel}
        </Text>

        {renderTxnLine(
          [
            payment
              ? tenderLabel(
                  payment.card_brand,
                  payment.card_last4,
                  payment.method,
                )
              : null,
            primaryStaffName && primaryStaffName !== "Unknown"
              ? `Sold by ${primaryStaffName}`
              : null,
          ],
          event.transaction.reference_id,
          event.transaction.id,
          { size: "sm", c: "dark.6", mt: "xs" },
        )}

        {showPoints && (
          <Text size="sm" mt={4} c="dimmed">
            Points earned:{" "}
            {event.transaction.loyalty_points_earned != null
              ? event.transaction.loyalty_points_earned.toLocaleString("en-US")
              : "—"}
          </Text>
        )}

        <Text size="xs" c="dimmed" mt="md">
          {formatDateTime(card.created_dt, "MMMM D, YYYY h:mm A")}
        </Text>
      </Paper>
    );
  };

  const renderCard = (card: ActivityCard) => {
    switch (card.type) {
      case "package":
        return renderPackageCard(card);
      case "giftcard":
        return renderGiftCardCard(card);
      case "giftcardclaim":
        return renderClaimCard(card);
      case "quicksale":
        return renderQuickSaleCard(card);
      default:
        return renderAppointmentCard(card);
    }
  };

  const filterConfig: { key: ActivityType; label: string }[] = [
    { key: "all", label: "All" },
    { key: "appointments", label: "Appointments" },
    { key: "quicksales", label: "Quick Sales" },
    { key: "packages", label: "Packages" },
    { key: "giftcards", label: "Gift Cards" },
  ];

  return (
    <Paper
      className={styles.timelineShell}
      p="lg"
      radius="lg"
      shadow="sm"
      style={{ position: "relative" }}
    >
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
          <Text size="lg" fw={600} c="dark.8">
            Activity Timeline
          </Text>
        </Group>

        <ScrollArea
          type="scroll"
          scrollbarSize={6}
          className={styles.filterScroll}
        >
          <Group gap="xs" wrap="nowrap" pb={4}>
            {filterConfig.map((f) => (
              <Button
                key={f.key}
                size="compact-sm"
                variant={activeFilter === f.key ? "filled" : "default"}
                color={activeFilter === f.key ? "teal" : "gray"}
                className={styles.filterPill}
                onClick={() => setActiveFilter(f.key)}
              >
                {f.label}
              </Button>
            ))}
          </Group>
        </ScrollArea>

        {loading && cards.length === 0 ? (
          <Group justify="center" p="xl">
            <Loader size="sm" color="teal" />
          </Group>
        ) : filteredCards.length === 0 ? (
          <Paper withBorder p="xl" className={styles.emptyState}>
            <Text c="dimmed" ta="center">
              No activity found for this filter.
            </Text>
          </Paper>
        ) : (
          <Box className={styles.timeline}>
            {Object.entries(groupedCards).map(([monthKey, monthCards]) => (
              <Box key={monthKey} className={styles.monthGroup}>
                <Text
                  fw={700}
                  size="sm"
                  tt="uppercase"
                  className={styles.monthLabel}
                >
                  {monthKey}
                </Text>
                <Stack gap="md" className={styles.monthItems}>
                  {monthCards.map(renderCard)}
                </Stack>
              </Box>
            ))}
          </Box>
        )}

        {nextCursor && (
          <Button
            variant="subtle"
            color="teal"
            onClick={onLoadMore}
            loading={loadingMore}
            className={styles.seeMoreButton}
          >
            See More
          </Button>
        )}
      </Stack>
    </Paper>
  );
};

export default ActivityTimeline;
